import { TranslateService } from '@ngx-translate/core';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { MaterialService } from '../../services/material/material.service';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserMasterService } from '../../services/user-master/user-master.service';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import { MATERIAL_ORDER_PRIORITY } from '../../types/material-order-priority';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { LocalStorageNotificationMaterialOrderModel } from '../low-level/local-storage-notification-material-order.model';
import { MaterialCheckModel, ODataMaterialCheckOutModel } from '../low-level/material-check.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { MultilineTextModelOptions } from '../options/multiline-text-model-options';
import { RadioModelOptions } from '../options/radio-model-options';
import { TextModelOptions } from '../options/text-model-options';
import { MultilineTextModel, ODataTextLineOutModel } from '../primitive/multiline-text.model';
import { TextModel } from '../primitive/text.model';
import { ArrayModel } from './array.model';
import { CompositeModel } from './composite.model';
import { CseDopModel, ODataCseDopOutModel } from './cse-dop.model';
import { DropdownModel } from './dropdown.model';
import { RadioModel } from './radio.model';
import SHIPPING_PRIORITY = ConfigConstants.SHIPPING_PRIORITY;
import { AddressModel } from './address.model';

export class MaterialOrderModel extends CompositeModel {
    protected clientManager: ClientManagerInterface;
    protected configService: ConfigService;
    protected modelOptionsLoader: ModelOptionsLoaderService;
    protected translate: TranslateService;
    public readonly CUSTOMER_ADDRESS = 'customer';
    public readonly DOP_ADDRESS = 'dop';
    public readonly NO_ADDRESS = 'na';
    public deliveryOptions: RadioModel<TextModel>;
    public address: CseDopModel;
    public dopNr: TextModel;
    public priority: DropdownModel<TextModel>;
    public partSet: ArrayModel<MaterialCheckModel>;
    public textInfo: MultilineTextModel;
    public dopList: DropdownModel<CseDopModel>;
    public isDopAddress = false;
    public isCustomerAddressSaved = false;
    public subAddressCharLimit: number;
    public contactMaxCharLimit: number;
    public enableStatesOfUSADropdown: boolean;
    public isPaidDelivery = false;
    public customerApprover?: TextModel;

    public constructor(
        public notificationModel: NotificationModel,
        partSet?: MaterialCheckModel[],
        localStorageMaterialOrderModel?: LocalStorageNotificationMaterialOrderModel,
    ) {
        super('MaterialOrderModel', {});

        const injector = CustomInjector.getInjector();
        this.translate = injector.get(TranslateService);
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const valueHelperService = injector.get(ValueHelperService);
        this.configService = injector.get(ConfigService);
        const materialService = injector.get(MaterialService);
        const userMasterService = injector.get(UserMasterService);
        this.clientManager = injector.get(ClientManagerInstanceToken);

        this.subAddressCharLimit = this.configService.getMatRequestSubAddressCharLimit();
        this.contactMaxCharLimit = this.configService.getMatRequestContactCharLimit();
        this.enableStatesOfUSADropdown = this.configService.getEnableStatesOfUSADropdown();

        this.set(new TextModel('FrontId', this.configService.getUserFrontId()));
        this.set(new TextModel('MessagetypeCSE', ''));
        this.set(new TextModel('MessageVersion', ConfigConstants.messageVersion));
        this.set(new TextModel('NotificationNo', notificationModel.identifier.qmNum));
        this.set(new TextModel('TaskNumber', notificationModel.identifier.maNum));

        if (
            this.configService.getDeliveryOptionsEnabled() &&
            this.notificationModel.notificationShippingPriorities?.length > 0
        ) {
            let defaultPriorityCode = '';
            const options = valueHelperService
                .getCreateMatOrderPriority()
                .reduce((optionsArray: Array<DisplayDecorator<TextModel>>, vh) => {
                    const priorityType = this.notificationModel.notificationShippingPriorities.find(
                        (priority) => priority.code === vh.code,
                    );
                    if (priorityType) {
                        if (priorityType.type === SHIPPING_PRIORITY.DEFAULT) {
                            defaultPriorityCode = priorityType.code;
                        }
                        optionsArray.push(
                            new DisplayDecorator(
                                priorityType.type + ' - ' + vh.descText,
                                new TextModel('TypeCode', vh.code),
                            ),
                        );
                    }
                    return optionsArray;
                }, []);
            options.sort((a, b) => a.displayName.localeCompare(b.displayName));
            this.priority = <DropdownModel<TextModel>>this.set(new DropdownModel<TextModel>('PriorityCode', options));

            if (defaultPriorityCode) {
                this.priority.selectByPredicate((option) => option.value === defaultPriorityCode);
            }

            this.customerApprover = new TextModel(
                'ZPAD',
                '',
                new TextModelOptions({
                    minLength: this.configService.getDeliveryApprovalMinimumLength(),
                    required: true,
                }),
            );
        } else {
            const options = valueHelperService
                .getCreateMatOrderPriority()
                .map((vh) => new DisplayDecorator(vh.descText, new TextModel('TypeCode', vh.code)));
            this.priority = <DropdownModel<TextModel>>this.set(new DropdownModel<TextModel>('PriorityCode', options));
        }

        this.modelOptionsLoader.load(this.priority, 'CreateMaterialOrder', 'Priority');
        this.dopNr = <TextModel>this.set(new TextModel('DopNr'));
        this.modelOptionsLoader.load(this.dopNr, 'CreateMaterialOrder', 'DOPNumber');

        const dopList = this.clientManager.isNgmP58()
            ? materialService.getCSEDopList()
            : materialService.getFullDopList();
        this.dopList = new DropdownModel('DropOffPointsOptions', dopList);
        this.modelOptionsLoader.load(this.dopList, 'CreateMaterialOrder', 'DOPDropdown');

        // NGMAA wants the CSE dop preselected by default
        if (this.clientManager.isNgmAA()) {
            const dopNewParts = materialService.getCseDefaultDopForNewParts();
            if (dopNewParts) {
                this.dopList.selectByEntry(dopNewParts.model);
            }
        }

        const deliveryOptions: Array<DisplayDecorator<TextModel>> = [];
        deliveryOptions.push(
            new DisplayDecorator(
                <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_N/A'),
                new TextModel(this.NO_ADDRESS, this.NO_ADDRESS),
            ),
        );
        const materialOrderDeliveryOptions = this.configService.getMaterialOrderDeliveryOptions();
        if (materialOrderDeliveryOptions.includes(this.CUSTOMER_ADDRESS)) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_CUSTOMER'),
                    new TextModel(this.CUSTOMER_ADDRESS, this.CUSTOMER_ADDRESS),
                ),
            );
        }
        if (materialOrderDeliveryOptions.includes(this.DOP_ADDRESS)) {
            deliveryOptions.push(
                new DisplayDecorator(
                    <string>this.translate.instant('MATERIAL_DELIVERY_ADDRESS_OPTION_DOP'),
                    new TextModel(this.DOP_ADDRESS, this.DOP_ADDRESS, new TextModelOptions({ readonly: false })),
                ),
            );
        }
        // NGMAA wants the DOP option to be selected as default
        const secondDeliveryOptionValue = 2;
        const initialSelection = this.clientManager.isNgmAA() ? secondDeliveryOptionValue : 0;
        this.deliveryOptions = new RadioModel(
            'DeliveryAddress',
            deliveryOptions,
            new RadioModelOptions({ initialSelection, invalidSelection: 0 }),
        );

        this.address = new CseDopModel();
        this.address.setFieldValidations('CreateMaterialOrder');

        this.partSet = <ArrayModel<MaterialCheckModel>>this.set(new ArrayModel<MaterialCheckModel>('PartSet'));
        if (typeof partSet !== 'undefined' && partSet?.length > 0) {
            this.partSet.addModels(partSet);
        }
        this.textInfo = <MultilineTextModel>this.set(new MultilineTextModel('LtextSet'));
        if (this.clientManager.isNgmAP0()) {
            userMasterService.setSalesOrgAndPlantForModel(this);
        }
        if (localStorageMaterialOrderModel) {
            this.deliveryOptions.selectByPredicate(
                (entry) => entry.name === localStorageMaterialOrderModel.deliveryOptions,
            );
            this.address = localStorageMaterialOrderModel.address;
            this.priority.selectByPredicate((entry) => entry.value === localStorageMaterialOrderModel.priority);
            this.textInfo.value = localStorageMaterialOrderModel.textInfo;
            if (this.customerApprover) {
                this.customerApprover.value = localStorageMaterialOrderModel.customerApprover ?? '';
            }
            this.partSet.value = localStorageMaterialOrderModel.materials.map(
                (material) =>
                    new MaterialCheckModel(
                        material.materialIdentifier.materialNumber.value,
                        material.quantity.value ? material.quantity.value : 1,
                        material.storageLocation.value,
                    ),
            );
            if (this.isCustomerAddress) {
                this.isCustomerAddressSaved = true;
            }
            if (
                this.deliveryOptions.selectedEntry.model.name === this.DOP_ADDRESS &&
                localStorageMaterialOrderModel.address.dropOffPoint.value
            ) {
                this.dopList.selectByPredicate(
                    (entry) =>
                        entry.model.dropOffPoint.value === localStorageMaterialOrderModel.address.dropOffPoint.value,
                );
            }
        }
        if (this.configService.getDeliveryOptionsEnabled()) {
            this.updateCustomerApproverVisibility();
        }
        this.updateTextInfoOptions();
        this.updateAddress();
    }

    public updateCustomerApproverVisibility(): void {
        this.isPaidDelivery = this.notificationModel.notificationShippingPriorities?.some((option) => {
            return this.priority.selectedEntry.value === option.code && option.type === SHIPPING_PRIORITY.PAYABLE;
        });
    }

    public updateTextInfoOptions(): void {
        this.textInfo.options = new MultilineTextModelOptions();
        this.modelOptionsLoader.load(this.textInfo, 'CreateMaterialOrder', 'LongTextInfo');
        const options = this.textInfo.options;
        const isTaxiSelected = this.priority.selectedEntry?.model.value === '0';
        const enableTextInfoCustomSettings = this.configService.getEnableTextInfoValidation() && isTaxiSelected;
        this.textInfo = assignModelOptions(
            this.textInfo,
            {
                ...this.textInfo.options,
                required: enableTextInfoCustomSettings ?? options.required,
                columns: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoLineLength()
                    : options.columns,
                lineLength: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoLineLength()
                    : options.lineLength,
                formatMultilineTextPattern: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoPattern()
                    : options.formatMultilineTextPattern,
                maxLines: enableTextInfoCustomSettings
                    ? this.configService.getMaterialOrderTextInfoMaxLines()
                    : options.maxLines,
                placeholder: enableTextInfoCustomSettings
                    ? <string>this.translate.instant('MATERIAL_ORDER_CREATE_TEXT_INFO_PLACEHOLDER')
                    : options.placeholder,
            },
            this,
        );
    }

    /**
     * Sub address : Department, Floor and Room
     * Calculates total length of Department, Floor and room fields and subtract with Max char limit to get remaining Length
     */
    public get remainingSubAddressLength(): number {
        const totalLengthUsed =
            this.address.department.value.length + this.address.room.value.length + this.address.floor.value.length;

        // While calculating remaining length of the whole sub address group, calculate remaining floor length too
        return this.subAddressCharLimit - totalLengthUsed;
    }

    /**
     * Contact : Phone and contact person
     * Calculates total length of Department, Floor and room fields and subtract with Max char limit to get remaining Length
     */
    public get remainingContactLength(): number {
        const totalLengthUsed = this.address.phone.value.length + this.address.contactPerson.value.length;
        return this.contactMaxCharLimit - totalLengthUsed;
    }

    /**
     * Used in HTML to show remaining length everytime user types something in SubAddress Department, floor and room
     * Example : Max. 28 char, Remaining 21 char
     */
    public get subAddressMaxCharLabel(): string {
        return this.isCustomerAddress
            ? this.translate.instant('MATERIAL_ORDER_SUB_ADDRESS_LIMIT_LABEL', {
                  maxChar: this.subAddressCharLimit,
                  totalRemaining: this.remainingSubAddressLength,
              })
            : '';
    }

    /**
     * Used in HTML to show remaining length everytime user types something in Phone and contact person
     * Example : Max. 29 char, Remaining 18 char.
     */
    public get contactMaxCharLabel(): string {
        return this.isCustomerAddress
            ? this.translate.instant('MATERIAL_ORDER_CONTACT_LIMIT_LABEL', {
                  maxChar: this.contactMaxCharLimit,
                  totalRemaining: this.remainingContactLength,
              })
            : '';
    }

    /**
     * Returns true, if selected deliveryOptions is CUSTOMER ADDRESS
     */
    public get isCustomerAddress(): boolean {
        return this.deliveryOptions.selectedEntry?.model.name === this.CUSTOMER_ADDRESS;
    }

    /**
     * The max length of sub address fields, i.e. Department, Floor, Room are updated dynamically
     * This function take care of updating the Max length for the fields
     *
     * @param textModel
     * @param maxLength
     * @private
     */
    private updateSubAddressMaxLengthProperty(textModel: TextModel, maxLength: number): TextModel {
        // Ensure the new max length does not go below the current length of the field
        return assignModelOptions(
            textModel,
            {
                ...textModel.options,
                maxLength: maxLength,
            },
            this.address,
        );
    }

    /**
     * The max length of sub address fields, i.e. Department, Floor, Room , Phone and Contact are updated dynamically
     * This function take care of updating the Max length for the fields
     *
     * @param textModel
     * @param remainingLength
     * @private
     */
    private updateMaxLengthProperty(textModel: TextModel, remainingLength: number): TextModel {
        // Ensure the new max length does not go below the current length of the field
        const maxLength = textModel.value.length + remainingLength;
        return assignModelOptions(
            textModel,
            {
                ...textModel.options,
                maxLength: maxLength,
            },
            this.address,
        );
    }

    /**
     * If combined length of department, Room  and Floor field value exceeds the limit then re-update the remaining Length as MaxLength for other fields
     */
    public checkMaxCharValidationOnSubAddress(): void {
        if (!this.isCustomerAddress) {
            return;
        }
        const totalLengthUsed =
            this.address.department.value.length + this.address.room.value.length + this.address.floor.value.length;
        const remainingLength = this.subAddressCharLimit - totalLengthUsed;

        this.address.setFieldValidations('CreateMaterialOrder');
        const departmentLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.department.options.maxLength;
        const roomLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.room.options.maxLength;
        const floorLength =
            totalLengthUsed > this.subAddressCharLimit ? remainingLength : this.address.floor.options.maxLength;

        this.address.department = this.updateSubAddressMaxLengthProperty(this.address.department, departmentLength);
        this.address.room = this.updateSubAddressMaxLengthProperty(this.address.room, roomLength);
        this.address.floor = this.updateSubAddressMaxLengthProperty(this.address.floor, floorLength);
    }

    /**
     * If combined length of Phone and Contact person field value exceeds the limit re-update the remaining Length as MaxLength for other fields
     */
    public checkMaxCharValidationOnContact(): void {
        if (this.isCustomerAddress) {
            const remainingLength = this.remainingContactLength;

            // Apply the remaining length to each field, ensuring it respects the current character count
            this.address.phone = this.updateMaxLengthProperty(this.address.phone, remainingLength);
            this.address.contactPerson = this.updateMaxLengthProperty(this.address.contactPerson, remainingLength);
        }
    }

    /**
     * This function sets the default values of address set to have NDP header address set
     * isCustomerAddressSaved is to check if User saved any address, if yes we have to load the saved address
     * If user switches from  CSE to DOP or N/a then , then we have to bind address from Notification Model
     */
    public updateAddress(): void {
        let dopModel: CseDopModel;
        this.isDopAddress = false;
        if (this.isCustomerAddress) {
            dopModel = this.isCustomerAddressSaved
                ? this.address
                : new CseDopModel(
                      '',
                      '',
                      this.notificationModel.address.name1.value,
                      this.notificationModel.address.name2.value,
                      this.notificationModel.address.name1shortP58,
                      this.notificationModel.address.name2shortP58,
                      this.notificationModel.address.street.value,
                      this.notificationModel.address.postCode.value,
                      this.notificationModel.address.city.value,
                      '',
                      this.notificationModel.address.phone.value,
                      this.notificationModel.address.department.value,
                      0,
                      '',
                      this.notificationModel.address.floor.value,
                      this.notificationModel.address.building.value,
                      this.notificationModel.address.room.value,
                  );
        } else if (this.deliveryOptions.selectedEntry.model.name === this.DOP_ADDRESS && this.dopList.selectedEntry) {
            this.isCustomerAddressSaved = false;
            dopModel = this.dopList.selectedEntry.model;
            this.isDopAddress = true;
        } else {
            this.isCustomerAddressSaved = false;
            dopModel = new CseDopModel();
        }

        this.address = dopModel;
        if (!this.isCustomerAddressSaved) {
            AddressModel.updateStateOfUSA(
                this.address,
                this.notificationModel.address.statesOfUSA?.selectedEntry?.model.value,
                this.isDopAddress,
            );
        }
        this.address.setFieldValidations('CreateMaterialOrder');
        this.checkMaxCharValidationOnSubAddress();
        this.checkMaxCharValidationOnContact();
    }

    public getOutModel(): ODataNotificationMaterialOrderOutModel {
        const outmodel = <ODataNotificationMaterialOrderOutModel>super.getOutModel();
        if (this.deliveryOptions.selectedEntry.model.name !== this.NO_ADDRESS) {
            outmodel.AddrSet = this.address.getOutModel();
            outmodel.DopNr = this.isCustomerAddress ? '' : this.dopList.selectedEntry.model.dropOffPoint.value;

            if (this.isDopAddress) {
                if (this.clientManager.isNgmP58()) {
                    // SHCNGMP58-1348 Only for NGMP58, REGION field should have the value of STATE field from DopSet
                    outmodel.AddrSet.Region = this.address.state.value;
                } else if (this.clientManager.isNgmAA()) {
                    // For NGM AA Region should be set to empty, as it is not expected on Gateway. Gateway only expect 3 char field for Region.
                    outmodel.AddrSet.Region = '';
                }
            }
            if (typeof this.address.statesOfUSA !== 'undefined') {
                outmodel.AddrSet.Region = this.address.state.value;
            }
            if (this.clientManager.isNgmSA()) {
                delete outmodel.AddrSet.Building;
                delete outmodel.AddrSet.Floor;
                delete outmodel.AddrSet.Region;
            }
        }
        if (this.isPaidDelivery && this.customerApprover) {
            outmodel.LtextSet.push({
                Counter: outmodel.LtextSet.length,
                TextLine: 'ZPAD: ' + this.customerApprover.value,
            });
        }
        return outmodel;
    }
}

export interface ODataNotificationMaterialOrderOutModel extends ODataNotificationIdentifierOutModel {
    MessagetypeCSE: string;
    MessageVersion: string;
    PriorityCode: string;
    DopNr: string;
    PartSet: ODataMaterialCheckOutModel[];
    LtextSet: ODataTextLineOutModel[];
    AddrSet: ODataCseDopOutModel;
}

export class LocalStorageMaterialOrderModel {
    public constructor(
        public notificationIdentifier: NotificationIdentifierModel,
        public priority: MATERIAL_ORDER_PRIORITY,
    ) {}
}

export class LocalStorageMaterialOrderModelFromJson extends LocalStorageMaterialOrderModel {
    public constructor(data: JsonLocalStorageMaterialOrderModel) {
        super(
            new NotificationIdentifierModelFromJson(data.notificationIdentifier),
            <MATERIAL_ORDER_PRIORITY>data.priority,
        );
    }
}

export interface JsonLocalStorageMaterialOrderModel {
    notificationIdentifier: JsonNotificationIdentifierModel;
    priority: string;
}
